(function venadaNavigationRuntime(global) {
  'use strict';

  if (global.VenadaNavigation) return;

  const OPEN_DELAY = 110;
  const CLOSE_DELAY = 150;
  const DESKTOP_MIN = 1024;
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function hiddenByTree(element, container) {
    for (let current = element; current; current = current.parentElement) {
      if (current.hidden || current.inert || current.hasAttribute?.('inert') || current.getAttribute?.('aria-hidden') === 'true') return true;
      const style = global.getComputedStyle?.(current);
      if (style?.display === 'none' || style?.visibility === 'hidden' || style?.visibility === 'collapse') return true;
      if (current === container) break;
    }
    return false;
  }

  function focusables(container) {
    return [...(container?.querySelectorAll(focusableSelector) ?? [])].filter((element) => {
      if (hiddenByTree(element, container)) return false;
      return typeof element.getClientRects !== 'function' || element.getClientRects().length > 0;
    });
  }

  function createController(root, options = {}) {
    const documentRef = options.document ?? global.document;
    const windowRef = options.window ?? global;
    const viewportWidth = options.viewportWidth ?? (() => windowRef.innerWidth);
    const setTimer = options.setTimer ?? global.setTimeout.bind(global);
    const clearTimer = options.clearTimer ?? global.clearTimeout.bind(global);
    const abortController = new AbortController();
    const listenerOptions = {signal: abortController.signal};
    const triggers = [...root.querySelectorAll('[data-venada-menu-trigger]')];
    const panels = [...root.querySelectorAll('[data-venada-menu-panel]')];
    const panelByKey = new Map(panels.map((panel) => [panel.dataset.venadaMenuPanel, panel]));
    const triggerByKey = new Map(triggers.map((trigger) => [trigger.dataset.venadaMenuTrigger, trigger]));
    const drawerOpener = root.querySelector('[data-venada-drawer-open]');
    const drawer = root.querySelector('[data-venada-mobile-drawer]');
    const drawerDialog = drawer?.querySelector('[role="dialog"]');
    const drawerBackdrop = root.querySelector('[data-venada-drawer-backdrop]');
    const drawerClose = root.querySelector('[data-venada-drawer-close]');
    const drawerSearch = drawer?.querySelector('[data-venada-drawer-search] button');
    const cartProxy = root.querySelector('[data-venada-cart-proxy]');
    const nativeCartTrigger = root.querySelector('[data-testid="cart-drawer-trigger"]');
    const nativeCartDialog = root.querySelector('[data-testid="cart-drawer-dialog"]');
    const accordionTriggers = [...root.querySelectorAll('[data-venada-accordion-trigger]')];
    let openKey = null;
    let openTimer = null;
    let closeTimer = null;
    let drawerOpen = false;
    let priorBodyOverflow = '';
    let lastMenuTrigger = null;
    let cartReturnPending = false;
    let cartFocusTimer = null;

    function cancelOpen() {
      if (openTimer !== null) clearTimer(openTimer);
      openTimer = null;
    }

    function cancelClose() {
      if (closeTimer !== null) clearTimer(closeTimer);
      closeTimer = null;
    }

    function closeMenu({restoreFocus = false} = {}) {
      cancelOpen();
      cancelClose();
      for (const panel of panels) panel.hidden = true;
      for (const trigger of triggers) trigger.setAttribute('aria-expanded', 'false');
      const focusTarget = openKey ? triggerByKey.get(openKey) : lastMenuTrigger;
      openKey = null;
      if (restoreFocus) focusTarget?.focus();
    }

    function openMenu(key, {focusFirst = false} = {}) {
      if (viewportWidth() < DESKTOP_MIN) return;
      cancelOpen();
      cancelClose();
      const panel = panelByKey.get(key);
      const trigger = triggerByKey.get(key);
      if (!panel || !trigger) return;
      for (const candidate of panels) candidate.hidden = candidate !== panel;
      for (const candidate of triggers) candidate.setAttribute('aria-expanded', candidate === trigger ? 'true' : 'false');
      openKey = key;
      lastMenuTrigger = trigger;
      if (focusFirst) (focusables(panel)[0] ?? panel).focus();
    }

    function toggleMenu(key) {
      if (openKey === key) closeMenu();
      else openMenu(key);
    }

    function scheduleOpen(key) {
      cancelOpen();
      cancelClose();
      openTimer = setTimer(() => {
        openTimer = null;
        openMenu(key);
      }, OPEN_DELAY);
    }

    function scheduleClose() {
      cancelOpen();
      cancelClose();
      closeTimer = setTimer(() => {
        closeTimer = null;
        closeMenu();
      }, CLOSE_DELAY);
    }

    function handleTriggerKey(trigger, event) {
      const key = trigger.dataset.venadaMenuTrigger;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openMenu(key, {focusFirst: true});
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu({restoreFocus: true});
      }
    }

    function handleRootKey(event) {
      if (event.key !== 'Escape') return;
      if (drawerOpen) {
        event.preventDefault();
        closeDrawer({restoreFocus: true});
      } else if (openKey) {
        event.preventDefault();
        closeMenu({restoreFocus: true});
      }
    }

    function openDrawer() {
      if (!drawer || viewportWidth() >= DESKTOP_MIN || drawerOpen) return;
      closeMenu();
      priorBodyOverflow = documentRef.body.style.overflow;
      documentRef.body.style.overflow = 'hidden';
      drawer.hidden = false;
      drawerOpen = true;
      drawerOpener?.setAttribute('aria-expanded', 'true');
      const initialFocus = drawerSearch ?? focusables(drawerDialog)[0];
      initialFocus?.focus();
    }

    function closeDrawer({restoreFocus = false} = {}) {
      if (!drawer) return;
      if (drawerOpen) documentRef.body.style.overflow = priorBodyOverflow;
      drawer.hidden = true;
      drawerOpen = false;
      drawerOpener?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) drawerOpener?.focus();
    }

    function handleDrawerKey(event, activeElement = documentRef.activeElement) {
      if (!drawerOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer({restoreFocus: true});
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables(drawerDialog);
      if (!items.length) return;
      const first = items[0];
      const last = items.at(-1);
      if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function handleResize() {
      if (viewportWidth() < DESKTOP_MIN) closeMenu();
      else closeDrawer();
    }

    function handleNativeCartClose() {
      if (!cartReturnPending) return;
      cartReturnPending = false;
      if (cartFocusTimer !== null) clearTimer(cartFocusTimer);
      cartFocusTimer = setTimer(() => {
        cartFocusTimer = null;
        if (viewportWidth() < DESKTOP_MIN) drawerOpener?.focus();
      }, 0);
    }

    for (const trigger of triggers) {
      const key = trigger.dataset.venadaMenuTrigger;
      trigger.addEventListener('pointerenter', () => scheduleOpen(key), listenerOptions);
      trigger.addEventListener('pointerleave', scheduleClose, listenerOptions);
      trigger.addEventListener('focus', () => scheduleOpen(key), listenerOptions);
      trigger.addEventListener('click', () => toggleMenu(key), listenerOptions);
      trigger.addEventListener('keydown', (event) => handleTriggerKey(trigger, event), listenerOptions);
    }

    for (const panel of panels) {
      panel.addEventListener('pointerenter', cancelClose, listenerOptions);
      panel.addEventListener('pointerleave', scheduleClose, listenerOptions);
      panel.addEventListener('focusin', cancelClose, listenerOptions);
      panel.addEventListener('focusout', (event) => {
        if (!root.contains(event.relatedTarget)) scheduleClose();
      }, listenerOptions);
    }

    root.addEventListener('keydown', handleRootKey, listenerOptions);
    drawerOpener?.addEventListener('click', openDrawer, listenerOptions);
    drawerClose?.addEventListener('click', () => closeDrawer({restoreFocus: true}), listenerOptions);
    drawerBackdrop?.addEventListener('click', () => closeDrawer({restoreFocus: true}), listenerOptions);
    drawerSearch?.addEventListener('click', () => closeDrawer(), listenerOptions);
    drawer?.addEventListener('keydown', (event) => handleDrawerKey(event), listenerOptions);

    for (const trigger of accordionTriggers) {
      trigger.addEventListener('click', () => {
        const key = trigger.dataset.venadaAccordionTrigger;
        const panel = root.querySelector(`[data-venada-accordion-panel="${key}"]`);
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (panel) panel.hidden = expanded;
      }, listenerOptions);
    }

    cartProxy?.addEventListener('click', () => {
      if (!nativeCartTrigger || !nativeCartDialog) {
        closeDrawer({restoreFocus: true});
        return;
      }
      if (cartFocusTimer !== null) {
        clearTimer(cartFocusTimer);
        cartFocusTimer = null;
      }
      cartReturnPending = true;
      closeDrawer();
      nativeCartTrigger.click();
    }, listenerOptions);
    nativeCartDialog?.addEventListener('close', handleNativeCartClose, listenerOptions);
    windowRef.addEventListener?.('resize', handleResize, listenerOptions);

    return {
      openMenu,
      toggleMenu,
      closeMenu,
      scheduleOpen,
      scheduleClose,
      handleTriggerKey,
      handleRootKey,
      openDrawer,
      closeDrawer,
      handleDrawerKey,
      handleResize,
      destroy() {
        abortController.abort();
        cartReturnPending = false;
        if (cartFocusTimer !== null) clearTimer(cartFocusTimer);
        cartFocusTimer = null;
        cancelOpen();
        cancelClose();
        closeMenu();
        closeDrawer();
      }
    };
  }

  const controllers = new WeakMap();

  function rootsIn(scope) {
    const roots = [...(scope?.querySelectorAll?.('[data-venada-navigation]') ?? [])];
    if (scope?.matches?.('[data-venada-navigation]')) roots.unshift(scope);
    return roots;
  }

  function initScope(scope = global.document) {
    for (const root of rootsIn(scope)) {
      if (!controllers.has(root)) controllers.set(root, createController(root));
    }
  }

  function destroyScope(scope) {
    for (const root of rootsIn(scope)) {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    }
  }

  const api = {OPEN_DELAY, CLOSE_DELAY, DESKTOP_MIN, hiddenByTree, focusables, createController, initScope, destroyScope};
  global.VenadaNavigation = api;

  if (global.document.readyState === 'loading') global.document.addEventListener('DOMContentLoaded', () => initScope(), {once: true});
  else initScope();
  global.document.addEventListener('shopify:section:load', (event) => initScope(event.target));
  global.document.addEventListener('shopify:section:unload', (event) => destroyScope(event.target));
})(window);

/* Fixed header surface toggle (Rooms We Keep pattern).
   A sentinel at the very top of the page is observed with IntersectionObserver;
   while it is visible the header is transparent, otherwise it turns solid.
   No scroll listener, so no main-thread work while scrolling. */
(() => {
  function initHeaderSurface() {
    const inner = document.querySelector('.venada-header-section');
    const group = inner && inner.closest('#header-group');
    if (!group || group.dataset.venadaSurfaceBound === 'true') return;
    group.dataset.venadaSurfaceBound = 'true';

    // Publish the real header height so body padding matches it exactly.
    const setOffset = () => {
      document.documentElement.style.setProperty('--venada-header-offset', group.offsetHeight + 'px');
    };
    setOffset();
    if ('ResizeObserver' in window) new ResizeObserver(setOffset).observe(group);

    let sentinel = document.getElementById('venada-header-sentinel');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.id = 'venada-header-sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;';
      document.body.prepend(sentinel);
    }

    // Set the correct surface immediately, so a reload part-way down the page
    // (or a suppressed observer callback in a background tab) still looks right.
    let solid = null;
    let lastScrollY = window.scrollY;
    const revealHeader = () => group.classList.remove('is-hidden');
    const syncFromScroll = () => {
      const currentY = Math.max(0, window.scrollY);
      const next = currentY > 0;
      if (next !== solid) {
        solid = next;
        group.classList.toggle('is-scrolled', next);
      }

      const delta = currentY - lastScrollY;
      const keepVisible = currentY <= group.offsetHeight
        || group.classList.contains('venada-menu-open')
        || group.matches(':focus-within');

      if (keepVisible || currentY < lastScrollY) {
        revealHeader();
      } else if (currentY > lastScrollY && delta >= 8) {
        group.classList.add('is-hidden');
      }
      lastScrollY = currentY;
    };
    syncFromScroll();

    // Always-on safety net: a boolean scrollY check costs nothing measurable and
    // keeps the surface correct even when observer callbacks are throttled
    // (background tab, restored session, reduced-power mode).
    window.addEventListener('scroll', syncFromScroll, { passive: true });
    document.addEventListener('visibilitychange', syncFromScroll);

    if (!('IntersectionObserver' in window)) return;

    new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      solid = !entry.isIntersecting;
      group.classList.toggle('is-scrolled', solid);
    }, { threshold: 0 }).observe(sentinel);

    // Keep the header opaque whenever a mega-menu or drawer is expanded.
    const syncMenuOpen = () => {
      const menuOpen = !!group.querySelector('[aria-expanded="true"]');
      group.classList.toggle('venada-menu-open', menuOpen);
      if (menuOpen) revealHeader();
    };
    new MutationObserver(syncMenuOpen).observe(group, {
      subtree: true, attributes: true, attributeFilter: ['aria-expanded']
    });
    syncMenuOpen();
    group.addEventListener('focusin', revealHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeaderSurface);
  } else {
    initHeaderSurface();
  }
  document.addEventListener('shopify:section:load', initHeaderSurface);
})();
